import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, toast } from '@/App';
import { Button } from '@/components/ui/button';
import { X, ArrowLeft } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { 
  PieChart, 
  Pie, 
  Cell, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer, 
  LineChart, 
  Line, 
  ReferenceLine
} from 'recharts';

// Section Analytics Component
const SectionAnalytics = ({ sectionData, sectionName, sectionLabel }) => {
  
  const COLORS = {
    Correct: '#22c55e',
    Incorrect: '#ef4444',
    Unanswered: '#d1d5db'
  };

  const preparePieData = (moduleStats) => {
    return [
      { name: 'Correct', value: moduleStats.correct },
      { name: 'Incorrect', value: moduleStats.incorrect },
      { name: 'Unanswered', value: moduleStats.omitted }
    ].filter(item => item.value > 0);
  };

  if (!sectionData) {
    return null;
  }
  

  const topicData = (sectionData.topic_breakdown || []).map(t => {
    let topicLabel = t.topic;
    if (topicLabel === 'Advanced Math' || topicLabel === 'AdvancedMath') topicLabel = 'Adv. Math';
    if (topicLabel === 'GeoTrig' || topicLabel === 'Geometry') topicLabel = 'Geo/Trig';
    if (topicLabel === 'PSD' || topicLabel === 'Problem Solving') topicLabel = 'Problem Solving';
    return {
      topic: topicLabel,
      accuracy: (t.accuracy * 100).toFixed(1)
    };
  });

  const difficultyData = ['easy', 'medium', 'hard'].map(diff => {
    const item = (sectionData.difficulty_breakdown || []).find(d => d.difficulty === diff);
    const total = item ? item.total : 0;
    const accuracy = item && total > 0 ? (item.accuracy * 100).toFixed(1) : 0;
    return {
      difficulty: diff.charAt(0).toUpperCase() + diff.slice(1),
      accuracy: parseFloat(accuracy),
      total: total,
      label: total > 0 ? `${accuracy}%` : 'N/A'
    };
  });

  // Time per question for Module 1
  const module1Qs = (sectionData.questions || []).filter(q => q.module === 'module_1');
  const timeM1Data = module1Qs
    .filter(q => q.user_answer)
    .map(q => ({
      question: `Q${q.question_number}`,
      time: q.time_spent,
      correct: q.is_correct ? 'Correct' : 'Incorrect'
    }));

  // Time per question for Module 2
  const module2Qs = (sectionData.questions || []).filter(q => 
    q.module === 'module_2a' || q.module === 'module_2b' || q.module === '2A' || q.module === '2B'
  );
  const timeM2Data = module2Qs
    .filter(q => q.user_answer)
    .map(q => ({
      question: `Q${q.question_number}`,
      time: q.time_spent,
      correct: q.is_correct ? 'Correct' : 'Incorrect'
    }));

  return (
    <div style={{ marginBottom: '3rem' }}>
      {/* Section Header */}
      <div style={{ 
        background: sectionName === 'rw' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        borderRadius: '12px', 
        padding: '1.5rem', 
        marginBottom: '1.5rem',
        color: 'white'
      }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0, fontFamily: 'Arial, sans-serif' }}>
          {sectionLabel}
        </h2>
        <div style={{ display: 'flex', gap: '2rem', marginTop: '1rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Raw Score</div>
            <div style={{ fontSize: '1.75rem', fontWeight: '700' }}>
              {sectionData.total_correct}/{sectionData.total_questions}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Accuracy</div>
            <div style={{ fontSize: '1.75rem', fontWeight: '700' }}>
              {(sectionData.accuracy * 100).toFixed(1)}%
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Total Time</div>
            <div style={{ fontSize: '1.75rem', fontWeight: '700' }}>
              {Math.floor(sectionData.total_time / 60)}m {Math.floor(sectionData.total_time % 60)}s
            </div>
          </div>
        </div>
      </div>

      {/* Module Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        {(sectionData.module_stats || []).map((mod, idx) => (
          <div key={`stat-${mod.module}`} style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem', fontFamily: 'Arial, sans-serif' }}>
              {sectionLabel} - {mod.module}
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#0030b3' }}>
              {mod.correct}/{mod.total}
            </div>
            <div style={{ fontSize: '0.85rem', color: '#999', fontFamily: 'Arial, sans-serif' }}>
              Time: {Math.floor(mod.time_used / 60)}m {Math.floor(mod.time_used % 60)}s
            </div>
          </div>
        ))}
      </div>

      {/* Module Outcome Pies */}
      {(sectionData.module_stats || []).length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
          {(sectionData.module_stats || []).map((mod, idx) => (
            <div key={`pie-${mod.module}`} style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', minHeight: '350px' }}>
              <div style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', fontFamily: 'Arial, sans-serif' }}>
                {sectionLabel} - {mod.module}
              </div>
              <div style={{ width: '100%', height: '280px' }}>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={preparePieData(mod)}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {preparePieData(mod).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[entry.name]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Topic and Difficulty - Side by Side */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', minHeight: '350px' }}>
          <div style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', fontFamily: 'Arial, sans-serif' }}>
            {sectionLabel} - Accuracy by Topic
          </div>
          <div style={{ width: '100%', height: `${Math.max(280, topicData.length * 50)}px` }}>
            <ResponsiveContainer width="100%" height={Math.max(280, topicData.length * 50)}>
              <BarChart data={topicData} layout="vertical" margin={{ top: 5, right: 20, left: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 100]} unit="%" />
                <YAxis type="category" dataKey="topic" width={120} />
                <Tooltip formatter={(value) => `${value}%`} />
                <Bar dataKey="accuracy" fill={sectionName === 'rw' ? '#667eea' : '#f59e0b'} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', minHeight: '350px' }}>
          <div style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', fontFamily: 'Arial, sans-serif' }}>
            {sectionLabel} - Accuracy by Difficulty
          </div>
          <div style={{ width: '100%', height: '280px' }}>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={difficultyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="difficulty" />
                <YAxis domain={[0, 100]} unit="%" />
                <Tooltip 
                  formatter={(value, name, props) => {
                    const total = props.payload.total;
                    if (total === 0) return ['No questions in this test', 'Accuracy'];
                    return [`${value}%`, 'Accuracy'];
                  }}
                  labelFormatter={(label) => {
                    const item = difficultyData.find(d => d.difficulty === label);
                    return `${label} (${item?.total || 0} questions)`;
                  }}
                />
                <Bar dataKey="accuracy" fill={sectionName === 'rw' ? '#667eea' : '#f59e0b'}>
                  {difficultyData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.total === 0 ? '#d1d5db' : (sectionName === 'rw' ? '#667eea' : '#f59e0b')} 
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          {difficultyData.some(d => d.total === 0) && (
            <div style={{ fontSize: '0.75rem', color: '#999', textAlign: 'center', marginTop: '0.5rem' }}>
              Gray bars indicate no questions of that difficulty in this test path
            </div>
          )}
        </div>
      </div>

      {/* Time per Question */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', minHeight: '350px' }}>
          <div style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', fontFamily: 'Arial, sans-serif' }}>
            {sectionLabel} - Time per Question (Module 1)
          </div>
          <div style={{ width: '100%', height: '280px' }}>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={timeM1Data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="question" angle={-45} textAnchor="end" height={60} />
                <YAxis label={{ value: 'Seconds', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' }, dy: 30 }} />
                <Tooltip />
                <Bar dataKey="time" fill="#0030b3">
                  {timeM1Data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.correct === 'Correct' ? '#22c55e' : '#ef4444'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', minHeight: '350px' }}>
          <div style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', fontFamily: 'Arial, sans-serif' }}>
            {sectionLabel} - Time per Question (Module 2)
          </div>
          <div style={{ width: '100%', height: '280px' }}>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={timeM2Data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="question" angle={-45} textAnchor="end" height={60} />
                <YAxis label={{ value: 'Seconds', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' }, dy: 30 }} />
                <Tooltip />
                <Bar dataKey="time" fill="#0030b3">
                  {timeM2Data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.correct === 'Correct' ? '#22c55e' : '#ef4444'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Topic Mastery Levels */}
      <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '1.5rem' }}>
        <div style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', fontFamily: 'Arial, sans-serif' }}>
          {sectionLabel} - Topic Mastery Levels
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
          {(sectionData.topic_breakdown || []).map((topic, idx) => (
            <div key={`topic-${topic.topic}`} style={{ 
              padding: '1rem', 
              borderRadius: '8px', 
              border: `2px solid ${topic.mastery === 'Strong' ? '#22c55e' : topic.mastery === 'Developing' ? '#f59e0b' : '#ef4444'}`,
              background: topic.mastery === 'Strong' ? '#f0fdf4' : topic.mastery === 'Developing' ? '#fffbeb' : '#fef2f2'
            }}>
              <div style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '0.5rem', fontFamily: 'Arial, sans-serif' }}>
                {topic.topic}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ 
                  fontSize: '0.75rem', 
                  padding: '0.25rem 0.5rem', 
                  borderRadius: '4px',
                  background: topic.mastery === 'Strong' ? '#22c55e' : topic.mastery === 'Developing' ? '#f59e0b' : '#ef4444',
                  color: 'white',
                  fontFamily: 'Arial, sans-serif'
                }}>
                  {topic.mastery}
                </span>
                <span style={{ fontSize: '0.85rem', color: '#666', fontFamily: 'Arial, sans-serif' }}>
                  {topic.correct}/{topic.total} correct
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function Analytics() {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showExitDialog, setShowExitDialog] = useState(false);

  // Set page title based on test type
  useEffect(() => {
    if (analytics) {
      if (analytics.isDiagnostic) {
        document.title = 'Diagnostic Analytics | Practice4SAT';
      } else if (analytics.test_id) {
        const testNumber = analytics.test_id.replace('test_', '');
        document.title = `Practice Test ${testNumber} Analytics | Practice4SAT`;
      } else {
        document.title = 'Analytics | Practice4SAT';
      }
    }
  }, [analytics]);

  useEffect(() => {
    fetchAnalytics();
  }, [sessionId]);

  const fetchAnalytics = async (retryCount = 0) => {
    try {
      // Try diagnostic first
      try {
        const diagnosticResponse = await axios.get(`${API}/diagnostic/${sessionId}/analytics`);
        if (diagnosticResponse.data) {
          setAnalytics({ ...diagnosticResponse.data, isDiagnostic: true });
          setLoading(false);
          return;
        }
      } catch (error) {
        console.debug('Not a diagnostic session, loading regular test');
      }

      // Regular test session
      const sessions = await axios.get(`${API}/test-sessions/${sessionId}`);
      const testId = sessions.data.test_id;
      
      const analyticsResponse = await axios.get(`${API}/tests/${testId}/analytics/${sessionId}`);
      setAnalytics({ ...analyticsResponse.data, isDiagnostic: false, test_id: testId });
      setLoading(false);
    } catch (error) {
      // If session not found and this is first or second attempt, retry after delay
      if (retryCount < 2 && error.response?.status === 404) {
        setTimeout(() => fetchAnalytics(retryCount + 1), (retryCount + 1) * 1000);
        return;
      }
      
      toast.error('Failed to load analytics');
      navigate('/dashboard');
      setLoading(false);
    }
  };

  const handleExit = () => {
    navigate('/dashboard');
  };

  if (loading || !analytics) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading analytics...</div>
      </div>
    );
  }

  const rwAnalytics = analytics.section_analytics?.rw;
  const mathAnalytics = analytics.section_analytics?.math;
  
  // Debug logging

  // Exit button for analytics header
  const exitButton = (
    <Button 
      onClick={() => setShowExitDialog(true)}
      variant="outline"
      style={{ 
        background: 'white',
        color: '#0030b3',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}
    >
      <X size={20} />
      Exit to Dashboard
    </Button>
  );

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header 
        variant="minimal" 
        logoClickPath="/dashboard"
        rightContent={exitButton}
      />

      {/* Main Content */}
      <div style={{ flex: 1, background: '#f5f5f5' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '3rem 2rem' }}>
          {/* Title and Review Button */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
            <h1 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#0030b3', fontFamily: 'Arial, sans-serif', margin: 0 }}>
              {analytics.test_title} Analytics
            </h1>
            <Button 
              onClick={() => navigate(`/review/${sessionId}`)}
              style={{ background: '#0030b3', color: 'white' }}
            >
              Review Questions
            </Button>
          </div>

          {/* Overall Score Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
            <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem', fontFamily: 'Arial, sans-serif' }}>Total Score</div>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#0030b3' }}>{analytics.final_score}</div>
              <div style={{ fontSize: '0.85rem', color: '#999', fontFamily: 'Arial, sans-serif' }}>out of 1600</div>
              {analytics.score_ranges?.total && (
                <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem', background: '#f0f4ff', padding: '0.4rem 0.6rem', borderRadius: '4px' }}>
                  Range: {analytics.score_ranges.total.min} - {analytics.score_ranges.total.max}
                </div>
              )}
            </div>

            <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem', fontFamily: 'Arial, sans-serif' }}>Total Raw Score</div>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#0030b3' }}>
                {analytics.total_correct}/{analytics.total_questions}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#999', fontFamily: 'Arial, sans-serif' }}>
                Total Time: {Math.floor(analytics.total_time / 60)}m {Math.floor(analytics.total_time % 60)}s
              </div>
            </div>

            {rwAnalytics && (
              <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', color: 'white' }}>
                <div style={{ fontSize: '0.9rem', opacity: 0.9, marginBottom: '0.5rem', fontFamily: 'Arial, sans-serif' }}>Reading & Writing</div>
                <div style={{ fontSize: '2rem', fontWeight: '700' }}>
                  {rwAnalytics.total_correct}/{rwAnalytics.total_questions}
                </div>
                <div style={{ fontSize: '0.85rem', opacity: 0.9, fontFamily: 'Arial, sans-serif' }}>
                  {(rwAnalytics.accuracy * 100).toFixed(1)}% accuracy
                </div>
                {analytics.score_ranges?.rw && (
                  <div style={{ fontSize: '0.8rem', opacity: 0.9, marginTop: '0.5rem', background: 'rgba(255,255,255,0.2)', padding: '0.4rem 0.6rem', borderRadius: '4px' }}>
                    Est. Score: {analytics.score_ranges.rw.min}-{analytics.score_ranges.rw.max}
                  </div>
                )}
              </div>
            )}

            {mathAnalytics && (
              <div style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', color: 'white' }}>
                <div style={{ fontSize: '0.9rem', opacity: 0.9, marginBottom: '0.5rem', fontFamily: 'Arial, sans-serif' }}>Math</div>
                <div style={{ fontSize: '2rem', fontWeight: '700' }}>
                  {mathAnalytics.total_correct}/{mathAnalytics.total_questions}
                </div>
                <div style={{ fontSize: '0.85rem', opacity: 0.9, fontFamily: 'Arial, sans-serif' }}>
                  {(mathAnalytics.accuracy * 100).toFixed(1)}% accuracy
                </div>
                {analytics.score_ranges?.math && (
                  <div style={{ fontSize: '0.8rem', opacity: 0.9, marginTop: '0.5rem', background: 'rgba(255,255,255,0.2)', padding: '0.4rem 0.6rem', borderRadius: '4px' }}>
                    Est. Score: {analytics.score_ranges.math.min}-{analytics.score_ranges.math.max}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Reading & Writing Section Analytics */}
          {rwAnalytics && (
            <SectionAnalytics 
              sectionData={rwAnalytics} 
              sectionName="rw" 
              sectionLabel="Reading & Writing" 
            />
          )}

          {/* Math Section Analytics */}
          {mathAnalytics && (
            <SectionAnalytics 
              sectionData={mathAnalytics} 
              sectionName="math" 
              sectionLabel="Math" 
            />
          )}

          {/* Key Insights */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '2rem' }}>
            <div style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', fontFamily: 'Arial, sans-serif' }}>Key Insights</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem', fontFamily: 'Arial, sans-serif' }}>Time Efficiency</div>
                <div style={{ fontSize: '1.3rem', fontWeight: '600', color: '#0030b3' }}>
                  {((analytics.insights?.time_efficiency?.efficiency_rate || 0) * 100).toFixed(0)}%
                </div>
                <div style={{ fontSize: '0.75rem', color: '#666', fontFamily: 'Arial, sans-serif' }}>
                  {analytics.insights?.time_efficiency?.efficient_correct || 0} under 95s
                </div>
              </div>

              <div>
                <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem', fontFamily: 'Arial, sans-serif' }}>Consistency</div>
                <div style={{ fontSize: '1.3rem', fontWeight: '600', color: '#0030b3' }}>
                  {analytics.insights?.consistency_score || 0}%
                </div>
                <div style={{ fontSize: '0.75rem', color: '#666', fontFamily: 'Arial, sans-serif' }}>
                  Throughout test
                </div>
              </div>

              <div>
                <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem', fontFamily: 'Arial, sans-serif' }}>Pacing Issues</div>
                <div style={{ fontSize: '1.3rem', fontWeight: '600', color: (analytics.insights?.pacing?.fast_incorrect || 0) + (analytics.insights?.pacing?.slow_incorrect || 0) > 3 ? '#ef4444' : '#22c55e' }}>
                  {(analytics.insights?.pacing?.fast_incorrect || 0) + (analytics.insights?.pacing?.slow_incorrect || 0)}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#666', fontFamily: 'Arial, sans-serif' }}>
                  {analytics.insights?.pacing?.fast_incorrect || 0} rushed
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <Footer />

      {/* Exit Dialog */}
      <Dialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Return to Dashboard?</DialogTitle>
            <DialogDescription>
              You can always view your analytics from the dashboard.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
            <Button variant="outline" onClick={() => setShowExitDialog(false)} style={{ flex: 1 }}>
              Stay Here
            </Button>
            <Button onClick={handleExit} style={{ flex: 1, background: '#0030b3' }}>
              Go to Dashboard
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
